import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
//primereact
import { Tooltip } from "primereact/tooltip";
//components
import Modal from "../../../../components/modal/Modal";
//services
import { getLeadMessages, sendLeadMessage, getLeadMessageMedia } from "../../../../services/WhatsappConnection";
//styles
import "./ModalWhatsApp.css";
import { Button } from "primereact/button";
import { getUserName } from "../../../../utils/auth";

let speechRecognition = null;
let mediaRecorder = null;
let audioChunks = [];

const ModalWhatsApp = ({ visible, setVisible, leadId, leadName, leadPhone, leadProfilePic }) => {
  const queryClient = useQueryClient();
  const username = getUserName(); // Obtém o nome do usuário logado
  const [hasWhatsAppConnection, setHasWhatsAppConnection] = useState(true); // Novo estado para controlar se tem conexão
  const [connectionError, setConnectionError] = useState(null); // Estado para armazenar erro de conexão
  const [shouldStopPolling, setShouldStopPolling] = useState(false); // Controla se deve parar o polling
  const isManualRetryRef = useRef(false); // Controla se é uma tentativa manual (usando ref para acessar no React Query)
  const [newMessage, setNewMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attachmentType, setAttachmentType] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingMode, setRecordingMode] = useState("audio"); // 'text' ou 'audio'
  const [recordingTime, setRecordingTime] = useState(0);
  const [sendAsDocument, setSendAsDocument] = useState(false); // Para controlar se envia como documento
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false); // Para mostrar menu de anexos
  // Estados para visualização de imagens
  const [imageViewer, setImageViewer] = useState({ visible: false, url: null, title: null });
  const [loadingMedia, setLoadingMedia] = useState({}); // Para controlar loading de mídias específicas
  const [cachedMediaUrls, setCachedMediaUrls] = useState({}); // Cache de URLs de mídia carregadas
  // Estados para controle de áudio
  const [audioPlayers, setAudioPlayers] = useState({}); // Para controlar cada player de áudio
  const [audioLoading, setAudioLoading] = useState({}); // Para controlar loading de áudios específicos
  // Estados para paginação de mensagens
  const [offset, setOffset] = useState(0);
  const [allMessages, setAllMessages] = useState([]);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [loadedOffsets, setLoadedOffsets] = useState(new Set([0])); // Controla quais offsets já foram carregados
  const [messagesByOffset, setMessagesByOffset] = useState(new Map()); // Armazena mensagens por offset
  const [hasMoreMessages, setHasMoreMessages] = useState(true); // Controla se há mais mensagens para carregar
  const [isFirstTimeOpening, setIsFirstTimeOpening] = useState(true); // Controla primeira abertura da modal
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const recordingIntervalRef = useRef(null);

  const scrollToBottom = (force = false) => {
    // Só faz scroll para baixo se for a primeira vez abrindo a modal
    if (!isFirstTimeOpening && !force) {
      return;
    }
    
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: force ? "auto" : "smooth",
          block: "end"
        });
      }
    }, force ? 0 : 100);
  };

  // React Query para buscar mensagens com polling (sempre offset 0 para mensagens mais recentes)
  const { 
    data: messagesResponse, 
    isLoading: isLoadingMessages, 
    error: messagesError,
    refetch: refetchMessages
  } = useQuery(
    ['leadMessages', leadId],
    () => getLeadMessages(leadId, 0),
    {
      enabled: Boolean(visible && leadId),
      refetchInterval: (visible && !shouldStopPolling) ? 10000 : false, // Polling apenas se não deve parar
      refetchIntervalInBackground: false, // Não fazer polling quando em background
      refetchOnWindowFocus: false, // Não refetch ao focar janela
      retry: false, // Não tentar automaticamente em caso de erro
      staleTime: 5000, // Considerar dados válidos por 5 segundos
      onSuccess: (response) => {
        setHasWhatsAppConnection(true);
        setConnectionError(null);
        setShouldStopPolling(false); // Reabilita polling se estava parado
        isManualRetryRef.current = false; // Reset da tentativa manual
        
        // Armazenar mensagens do offset 0 (mais recentes)
        if (response.data && Array.isArray(response.data)) {
          const formattedMessages = response.data.map(msg => ({
            id: msg.id,
            serialized_id: msg.serialized_id,
            from: msg.from_user_id,
            to: msg.to_user_id,
            body: msg.body,
            timestamp: msg.message_timestamp,
            type: msg.type,
            isForwarded: msg.is_forwarded,
            hasMedia: msg.has_media,
            remote_id: msg.remote_id,
            from_me: msg.from_me,
            ack: msg.ack,
            whatsapp_connection_id: msg.whatsapp_connection_id,
            created_at: msg.created_at,
            updated_at: msg.updated_at,
            location_latitude: msg.location_latitude,
            location_longitude: msg.location_longitude,
            location_name: msg.location_name,
            location_address: msg.location_address,
            location_url: msg.location_url,
            location_description: msg.location_description,
            sent_by_user: msg.sent_by_user
          })).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
          
          setMessagesByOffset(prev => new Map(prev.set(0, formattedMessages)));
        }
      },
      onError: (error) => {
        console.error("Erro ao carregar mensagens:", error);
        
        if (error.statusCode === 400 && error.apiMessage) {
          setHasWhatsAppConnection(false);
          setConnectionError(error.apiMessage);
          
          // Para o polling apenas se não for uma tentativa manual
          if (!isManualRetryRef.current) {
            setShouldStopPolling(true);
          } else {
            // Se for tentativa manual e ainda deu erro, para o polling também
            setShouldStopPolling(true);
            isManualRetryRef.current = false;
          }
        } else {
          setConnectionError("Erro ao carregar mensagens. Tente novamente.");
          // Não para o polling para outros tipos de erro, mas reseta tentativa manual
          isManualRetryRef.current = false;
        }
      }
    }
  );

  // Reset das mensagens quando o modal abre/fecha ou muda de lead
  useEffect(() => {
    if (visible && leadId) {
      setAllMessages([]);
      setOffset(0);
      setLoadedOffsets(new Set([0]));
      setMessagesByOffset(new Map());
      setHasMoreMessages(true);
      setIsFirstTimeOpening(true); // Resetar flag de primeira vez
      
      // Scroll para baixo após um pequeno delay para garantir que o modal esteja renderizado
      setTimeout(() => {
        scrollToBottom(true);
        // Após primeiro scroll, marcar que não é mais primeira vez
        setTimeout(() => setIsFirstTimeOpening(false), 1000);
      }, 300);
    }
  }, [visible, leadId]);

  // Atualizar allMessages quando messagesByOffset muda
  useEffect(() => {
    // Concatenar todas as mensagens de todos os offsets carregados
    const allMessagesArray = [];
    for (let i = Math.max(...Array.from(loadedOffsets)); i >= 0; i--) {
      if (messagesByOffset.has(i)) {
        const offsetMessages = messagesByOffset.get(i);
        allMessagesArray.unshift(...offsetMessages); // Adicionar no início (mensagens mais antigas primeiro)
      }
    }
    
    // Remover duplicatas por ID e ordenar por timestamp
    const uniqueMessages = allMessagesArray.reduce((acc, message) => {
      if (!acc.find(msg => msg.id === message.id)) {
        acc.push(message);
      }
      return acc;
    }, []).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    setAllMessages(uniqueMessages);
  }, [messagesByOffset, loadedOffsets]);

  // Scroll para baixo quando a conversa abre ou quando há novas mensagens
  useEffect(() => {
    if (visible && allMessages.length > 0 && isFirstTimeOpening) {
      // Scroll imediato ao abrir a conversa pela primeira vez
      const isFirstLoad = loadedOffsets.size === 1 && loadedOffsets.has(0);
      if (isFirstLoad) {
        scrollToBottom(true);
      }
    }
  }, [visible, allMessages.length, loadedOffsets, isFirstTimeOpening]);

  // Scroll suave apenas para novas mensagens do offset 0 (mais recentes) - só na primeira vez
  useEffect(() => {
    if (allMessages.length > 0 && messagesByOffset.has(0) && isFirstTimeOpening) {
      const recentMessages = messagesByOffset.get(0);
      const lastMessage = allMessages[allMessages.length - 1];
      
      // Só faz scroll se a última mensagem é do offset 0 (mensagens recentes)
      const isRecentMessage = recentMessages.some(msg => msg.id === lastMessage.id);
      
      // E só se não estamos carregando mensagens antigas
      if (isRecentMessage && !loadingMoreMessages) {
        scrollToBottom();
      }
    }
  }, [allMessages, messagesByOffset, loadingMoreMessages, isFirstTimeOpening]);

  // Mutation para carregar mais mensagens antigas
  const loadMoreMessagesMutation = useMutation({
    mutationFn: async (currentOffset) => {
      return await getLeadMessages(leadId, currentOffset);
    },
    onSuccess: (response, currentOffset) => {
      if (response.data && Array.isArray(response.data)) {
        const formattedMessages = response.data.map(msg => ({
          id: msg.id,
          serialized_id: msg.serialized_id,
          from: msg.from_user_id,
          to: msg.to_user_id,
          body: msg.body,
          timestamp: msg.message_timestamp,
          type: msg.type,
          isForwarded: msg.is_forwarded,
          hasMedia: msg.has_media,
          remote_id: msg.remote_id,
          from_me: msg.from_me,
          ack: msg.ack,
          whatsapp_connection_id: msg.whatsapp_connection_id,
          created_at: msg.created_at,
          updated_at: msg.updated_at,
          location_latitude: msg.location_latitude,
          location_longitude: msg.location_longitude,
          location_name: msg.location_name,
          location_address: msg.location_address,
          location_url: msg.location_url,
          location_description: msg.location_description,
          sent_by_user: msg.sent_by_user
        })).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        // Se não há mensagens neste offset, não há mais mensagens para carregar
        if (formattedMessages.length === 0) {
          setHasMoreMessages(false);
          return;
        }
        
        // Armazenar mensagens do offset atual
        setMessagesByOffset(prev => new Map(prev.set(currentOffset, formattedMessages)));
        
        // Marcar offset como carregado
        setLoadedOffsets(prev => new Set(prev.add(currentOffset)));
      } else {
        // Se a resposta não tem dados, não há mais mensagens
        setHasMoreMessages(false);
      }
    },
    onError: (error) => {
      console.error("Erro ao carregar mais mensagens:", error);
    }
  });

  // Função para carregar mais mensagens
  const loadMoreMessages = async () => {
    if (loadingMoreMessages || loadMoreMessagesMutation.isLoading) return;
    
    // Encontrar o próximo offset que ainda não foi carregado
    let nextOffset = 1;
    while (loadedOffsets.has(nextOffset)) {
      nextOffset++;
    }
    
    setLoadingMoreMessages(true);
    
    try {
      await loadMoreMessagesMutation.mutateAsync(nextOffset);
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  // Mutation simplificada para enviar mensagem
  const sendMessageMutation = useMutation({
    mutationFn: async ({ leadId, formData }) => {
      console.log('🚀 Iniciando envio da mensagem...');
      const result = await sendLeadMessage(leadId, formData);
      console.log('✅ Mensagem enviada com sucesso:', result);
      return result;
    },
    onSuccess: () => {
      console.log('🎉 Mutation onSuccess executado');
      // Atualizar mensagens após envio bem-sucedido
      refetchMessages();
    },
    onError: (error) => {
      console.error('❌ Erro na mutation:', error);
      if (error.statusCode === 400 && error.apiMessage) {
        setHasWhatsAppConnection(false);
        setConnectionError(error.apiMessage);
        alert(`Erro: ${error.apiMessage}`);
      } else {
        alert('Erro ao enviar mensagem. Tente novamente.');
      }
    }
  });

  // Usar allMessages como fonte principal de mensagens
  const messages = allMessages;

  // Função para carregar mídia de uma mensagem
  const loadMessageMedia = async (messageId) => {
    try {
      const response = await getLeadMessageMedia(leadId, messageId);
      if (response.data && response.data.media_temporary_url) {
        return response.data.media_temporary_url;
      }
      return null;
    } catch (error) {
      console.error("Erro ao carregar mídia:", error);
      return null;
    }
  };

  // Função para abrir visualizador de imagem
  const openImageViewer = async (messageId, messageType, messageBody) => {
    setLoadingMedia(prev => ({ ...prev, [messageId]: true }));
    
    try {
      // Verificar se já temos a URL em cache
      let mediaUrl = cachedMediaUrls[messageId];
      
      if (!mediaUrl) {
        // Se não tiver em cache, carregar da API
        mediaUrl = await loadMessageMedia(messageId);
        if (mediaUrl) {
          // Armazenar no cache
          setCachedMediaUrls(prev => ({
            ...prev,
            [messageId]: mediaUrl
          }));
        }
      }
      
      if (mediaUrl) {
        setImageViewer({ 
          visible: true, 
          url: mediaUrl, 
          title: messageBody || `${messageType.charAt(0).toUpperCase() + messageType.slice(1)}`,
          type: messageType,
          messageId: messageId // Adicionar messageId para download
        });
      } else {
        alert('Erro ao carregar mídia. Tente novamente.');
      }
    } catch (error) {
      console.error("Erro ao abrir visualizador:", error);
      alert('Erro ao carregar mídia. Tente novamente.');
    } finally {
      setLoadingMedia(prev => ({ ...prev, [messageId]: false }));
    }
  };

  // Função para fechar visualizador de imagem
  const closeImageViewer = () => {
    setImageViewer({ visible: false, url: null, title: null, type: null, messageId: null });
  };

  // Função para baixar mídia
  const downloadMedia = async (messageId, messageType, messageBody) => {
    try {
      const mediaUrl = await loadMessageMedia(messageId);
      if (mediaUrl) {
        const link = document.createElement('a');
        link.href = mediaUrl;
        link.download = `${messageType}_${Date.now()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('Erro ao baixar mídia. Tente novamente.');
      }
    } catch (error) {
      console.error("Erro ao baixar mídia:", error);
      alert('Erro ao baixar mídia. Tente novamente.');
    }
  };

  // Função para lidar com áudio
  const handleAudioPlay = async (serializedId, messageType, body) => {
    console.log('=== handleAudioPlay INICIADO ===');
    console.log('Parâmetros:', { serializedId, messageType, body });
    console.log('Cache atual:', cachedMediaUrls[serializedId]);
    console.log('Player atual:', audioPlayers[serializedId]);
    
    try {
      // Se já existe URL em cache, apenas controla reprodução
      if (cachedMediaUrls[serializedId]) {
        console.log('URL encontrada em cache, controlando reprodução...');
        const audio = audioPlayers[serializedId];
        
        if (audio) {
          console.log('Player encontrado, estado atual:', { paused: audio.paused, currentTime: audio.currentTime });
          
          if (audio.paused) {
            console.log('Áudio pausado, iniciando reprodução...');
            // Pausar outros áudios em reprodução
            Object.entries(audioPlayers).forEach(([id, player]) => {
              if (player && !player.paused && player !== audio) {
                console.log('Pausando outro áudio:', id);
                player.pause();
              }
            });
            
            try {
              await audio.play();
              console.log('Áudio iniciado com sucesso!');
            } catch (playError) {
              console.error('Erro ao reproduzir áudio:', playError);
            }
          } else {
            console.log('Áudio tocando, pausando...');
            audio.pause();
          }
        } else {
          console.log('URL em cache mas sem player, criando novo player...');
          // Criar novo player de áudio
          const newAudio = new Audio(cachedMediaUrls[serializedId]);
          
          newAudio.onloadeddata = () => {
            console.log('Áudio carregado, duração:', newAudio.duration);
            setAudioPlayers(prev => ({
              ...prev,
              [serializedId]: newAudio
            }));
          };
          
          newAudio.onended = () => {
            console.log('Áudio finalizado');
            setAudioPlayers(prev => {
              const updated = { ...prev };
              if (updated[serializedId]) {
                updated[serializedId].currentTime = 0;
              }
              return updated;
            });
          };
          
          newAudio.onerror = (error) => {
            console.error('Erro no player de áudio:', error);
            setAudioPlayers(prev => {
              const updated = { ...prev };
              delete updated[serializedId];
              return updated;
            });
          };
          
          // Pausar outros áudios em reprodução
          Object.entries(audioPlayers).forEach(([id, player]) => {
            if (player && !player.paused) {
              console.log('Pausando outro áudio antes de criar novo:', id);
              player.pause();
            }
          });
          
          setAudioPlayers(prev => ({
            ...prev,
            [serializedId]: newAudio
          }));
          
          try {
            await newAudio.play();
            console.log('Novo áudio iniciado com sucesso!');
          } catch (playError) {
            console.error('Erro ao reproduzir novo áudio:', playError);
          }
        }
        return;
      }

      // Se não tem URL, buscar do servidor
      console.log('URL não encontrada em cache, carregando do servidor...');
      setAudioLoading(prev => ({ ...prev, [serializedId]: true }));
      
      try {
        const mediaUrl = await loadMessageMedia(serializedId);
        console.log('URL recebida do servidor:', mediaUrl);
        
        if (mediaUrl) {
          // Salvar no cache
          setCachedMediaUrls(prev => ({
            ...prev,
            [serializedId]: mediaUrl
          }));
          console.log('URL salva no cache');

          // Criar e reproduzir áudio
          const audio = new Audio(mediaUrl);
          
          audio.onloadeddata = () => {
            console.log('Novo áudio carregado do servidor, duração:', audio.duration);
            setAudioPlayers(prev => ({
              ...prev,
              [serializedId]: audio
            }));
          };
          
          audio.onended = () => {
            console.log('Áudio do servidor finalizado');
            setAudioPlayers(prev => {
              const updated = { ...prev };
              if (updated[serializedId]) {
                updated[serializedId].currentTime = 0;
              }
              return updated;
            });
          };
          
          audio.onerror = (error) => {
            console.error('Erro no player de áudio do servidor:', error);
          };
          
          // Pausar outros áudios em reprodução
          Object.entries(audioPlayers).forEach(([id, player]) => {
            if (player && !player.paused) {
              console.log('Pausando outro áudio antes de reproduzir do servidor:', id);
              player.pause();
            }
          });
          
          setAudioPlayers(prev => ({
            ...prev,
            [serializedId]: audio
          }));
          
          try {
            await audio.play();
            console.log('Áudio do servidor iniciado com sucesso!');
          } catch (playError) {
            console.error('Erro ao reproduzir áudio do servidor:', playError);
          }
        } else {
          console.error('URL de mídia não encontrada');
          alert('Não foi possível carregar o áudio. Tente novamente.');
        }

      } catch (error) {
        console.error('Erro ao carregar áudio do servidor:', error);
        alert('Erro ao carregar áudio: ' + error.message);
      } finally {
        setAudioLoading(prev => ({ ...prev, [serializedId]: false }));
        console.log('Loading finalizado');
      }
    } catch (error) {
      console.error('Erro geral na função handleAudioPlay:', error);
      setAudioLoading(prev => ({ ...prev, [serializedId]: false }));
      alert('Erro ao reproduzir áudio: ' + error.message);
    }
    
    console.log('=== handleAudioPlay FINALIZADO ===');
  };

  const startRecording = () => {
    if (recordingMode === "text") {
      startTextRecording();
    } else {
      startAudioRecording();
    }
  };

  const stopRecording = () => {
    if (recordingMode === "text") {
      stopTextRecording();
    } else {
      stopAudioRecording();
    }
  };

  const startTextRecording = () => {
    setIsRecording(true);

    const isSpeechRecognitionAPIAvailable =
      "SpeechRecognition" in window || "webkitSpeechRecognition" in window;

    if (!isSpeechRecognitionAPIAvailable) {
      alert(
        "Infelizmente seu navegador não suporta a API de reconhecimento de voz"
      );
      setIsRecording(false);
      return;
    }

    const SpeechRecognitionAPI =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    speechRecognition = new SpeechRecognitionAPI();
    speechRecognition.lang = "pt-BR";
    speechRecognition.continuous = true;
    speechRecognition.maxAlternatives = 1;
    speechRecognition.interimResults = true;

    speechRecognition.onresult = (event) => {
      const transcription = Array.from(event.results).reduce((text, result) => {
        return text.concat(result[0].transcript);
      }, "");

      setNewMessage(transcription);
      console.log("Texto reconhecido:", transcription);
    };

    speechRecognition.onerror = (event) => {
      console.error("Erro no reconhecimento de voz:", event.error);
      setIsRecording(false);

      switch (event.error) {
        case "not-allowed":
          alert(
            "Permissão de microfone negada. Por favor, permita o acesso ao microfone."
          );
          break;
        case "no-speech":
          console.log("Nenhuma fala detectada, continuando...");
          break;
        case "audio-capture":
          alert(
            "Erro ao capturar áudio. Verifique se o microfone está funcionando."
          );
          break;
        case "network":
          alert("Erro de rede. Verifique sua conexão com a internet.");
          break;
        default:
          alert("Erro no reconhecimento de voz: " + event.error);
      }
    };

    speechRecognition.onend = () => {
      setIsRecording(false);
      console.log("Gravação de texto finalizada");
    };

    speechRecognition.start();
    console.log("Gravação de texto iniciada");
  };

  const stopTextRecording = () => {
    setIsRecording(false);

    if (speechRecognition !== null) {
      speechRecognition.stop();
      console.log("Parando gravação de texto...");
    }
  };

  const startAudioRecording = async () => {
    try {
      setIsRecording(true);
      setRecordingTime(0);
      audioChunks = [];

      // Solicitar permissão para usar o microfone
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Criar o MediaRecorder
      mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus", // Formato compatível com a maioria dos navegadores
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Criar o blob do áudio
        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });

        // Criar URL para preview
        const audioUrl = URL.createObjectURL(audioBlob);

        // Criar arquivo simulado para envio
        const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
          type: "audio/webm",
        });

        // Definir como anexo PTT (Push-to-Talk)
        setAttachmentType("ptt");
        setAttachment({
          url: audioUrl,
          name: audioFile.name,
          size: formatFileSize(audioFile.size),
          type: audioFile.type,
          duration: recordingTime,
          file: audioFile, // Arquivo real para envio ao backend
        });

        // Parar todas as tracks do stream
        stream.getTracks().forEach((track) => track.stop());

        setIsRecording(false);
        if (recordingIntervalRef.current) {
          clearInterval(recordingIntervalRef.current);
        }

        console.log("Gravação de áudio finalizada");
      };

      mediaRecorder.onerror = (event) => {
        console.error("Erro na gravação de áudio:", event.error);
        setIsRecording(false);
        alert("Erro durante a gravação de áudio");
      };

      // Iniciar gravação
      mediaRecorder.start();

      // Contador de tempo
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      console.log("Gravação de áudio iniciada");
    } catch (error) {
      console.error("Erro ao acessar microfone:", error);
      setIsRecording(false);

      if (error.name === "NotAllowedError") {
        alert(
          "Permissão de microfone negada. Por favor, permita o acesso ao microfone."
        );
      } else if (error.name === "NotFoundError") {
        alert(
          "Microfone não encontrado. Verifique se há um microfone conectado."
        );
      } else {
        alert("Erro ao acessar o microfone: " + error.message);
      }
    }
  };

  const stopAudioRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      console.log("Parando gravação de áudio...");
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Carregar mensagens quando o modal abrir e tiver leadId
  useEffect(() => {
    if (visible && leadId) {
      // Resetar estados quando trocar de lead ou abrir o modal
      setHasWhatsAppConnection(true);
      setConnectionError(null);
      setShouldStopPolling(false); // Resetar controle de polling
      isManualRetryRef.current = false; // Resetar tentativa manual
      setNewMessage("");
      setAttachment(null);
      setAttachmentType(null);
      setSendAsDocument(false);
      setCachedMediaUrls({}); // Limpar cache de mídia quando trocar de lead
      
      // As mensagens serão carregadas automaticamente pelo React Query
    }
  }, [visible, leadId]);

  // Fechar menu de anexos quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAttachmentMenu &&
        !event.target.closest(".attachment-menu-container")
      ) {
        setShowAttachmentMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAttachmentMenu]);

  // Cleanup - parar gravação se o modal for fechado
  useEffect(() => {
    if (!visible && isRecording) {
      stopRecording();
    }

    // Parar polling quando modal for fechado
    if (!visible) {
      queryClient.cancelQueries(['leadMessages', leadId]);
    }

    // Cleanup quando componente for desmontado
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorder && mediaRecorder.state === "recording") {
        mediaRecorder.stop();
      }
      if (speechRecognition) {
        speechRecognition.stop();
      }
    };
  }, [visible, isRecording, queryClient, leadId]);

  // Controlar fechamento do modal de imagem com ESC
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && imageViewer.visible) {
        closeImageViewer();
      }
    };

    if (imageViewer.visible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [imageViewer.visible]);

  // Limpar áudios quando o componente for desmontado ou modal fechado
  useEffect(() => {
    return () => {
      // Pausar e limpar todos os áudios
      Object.values(audioPlayers).forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
        }
      });
      setAudioPlayers({});
      
      // Limpar URLs temporárias de áudio
      Object.values(cachedMediaUrls).forEach(url => {
        if (url && url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [visible]);

  // Pausar áudios quando o modal fechar
  useEffect(() => {
    if (!visible) {
      Object.values(audioPlayers).forEach(audio => {
        if (audio && !audio.paused) {
          audio.pause();
        }
      });
    }
  }, [visible, audioPlayers]);

  const formatTimestamp = (timestamp) => {
    // O timestamp agora vem como string ISO (2025-08-18T20:06:23.000000Z)
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return "agora";
    if (minutes < 60) return `${minutes}min`;
    if (hours < 24)
      return date.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    if (days < 7)
      return date.toLocaleDateString("pt-BR", {
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Função para pegar apenas os 2 primeiros nomes
  const getTruncatedName = (fullName) => {
    if (!fullName) return fullName;
    const nameParts = fullName.trim().split(' ');
    return nameParts.slice(0, 2).join(' ');
  };

  const getContactName = (from, fromMe = false, message = null) => {
    if (fromMe) {
      // Para mensagens enviadas por mim, verificar se tem nome do usuário que enviou
      if (message && message.sent_by_user && message.sent_by_user.name) {
        return getTruncatedName(message.sent_by_user.name);
      }
      return "Você";
    }
    return getTruncatedName(leadName) || "Cliente";
  };

  const getContactAvatar = (from, fromMe = false, message = null) => {
    if (fromMe) {
      // Para mensagens enviadas por mim, verificar se tem foto do usuário que enviou
      if (message && message.sent_by_user && message.sent_by_user.profile_photo && message.sent_by_user.profile_photo.path) {
        return message.sent_by_user.profile_photo.path;
      }
      return `https://ui-avatars.com/api/?name=${username}&background=25d366&color=fff&size=40`;
    }
    
    // Se tem foto de perfil do lead, usa ela
    if (leadProfilePic) {
      return leadProfilePic;
    }
    
    // Caso contrário, usa o avatar gerado com as iniciais
    const name = leadName ? leadName.replace(' ', '+') : "Cliente";
    return `https://ui-avatars.com/api/?name=${name}&background=075e54&color=fff&size=40`;
  };

  const getMediaType = (file) => {
    const fileType = file.type;
    if (fileType.startsWith("image/")) return "image";
    if (fileType.startsWith("video/")) return "video";
    if (fileType.startsWith("audio/")) return "audio";
    return "document";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Função para renderizar o ícone de status baseado no campo 'ack'
  const renderMessageStatusIcon = (ackStatus) => {
    switch (ackStatus) {
      case -1: // ACK_ERROR
        return <i className="fa fa-exclamation-triangle message-status-error" title="Erro" style={{ color: '#f44336' }}></i>;
      case 0: // ACK_PENDING
        return <i className="fa fa-clock message-status-pending" title="Pendente" style={{ color: '#9e9e9e' }}></i>;
      case 1: // ACK_SERVER
        return <i className="fa fa-check message-status-server" title="Servidor" style={{ color: '#9e9e9e' }}></i>;
      case 2: // ACK_DEVICE
        return <i className="fa fa-check-double message-status-device" title="Dispositivo" style={{ color: '#9e9e9e' }}></i>;
      case 3: // ACK_READ
        return <i className="fa fa-check-double message-status-read" title="Lido" style={{ color: '#4fc3f7' }}></i>;
      case 4: // ACK_PLAYED
        return <i className="fa fa-check-double message-status-played" title="Reproduzido" style={{ color: '#4fc3f7' }}></i>;
      default:
        return <i className="fa fa-clock message-status-pending" title="Pendente" style={{ color: '#9e9e9e' }}></i>;
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Função para converter URL ou File para Blob
  const convertToBlob = async (fileOrUrl, fileName) => {
    if (fileOrUrl instanceof File) {
      return fileOrUrl;
    }

    try {
      const response = await fetch(fileOrUrl);
      const blob = await response.blob();
      return new File([blob], fileName, { type: blob.type });
    } catch (error) {
      console.error("Erro ao converter para blob:", error);
      return null;
    }
  };

  // Função para preparar FormData para envio
  const prepareFormData = async (messageText, attachmentData, messageType) => {
    console.log("Preparando FormData:", { messageText, attachmentData, messageType });
    
    const formData = new FormData();

    // Adicionar dados básicos da mensagem conforme a API
    formData.append("type", messageType);
    
    if (messageText && messageText.trim()) {
      formData.append("textMessage", messageText.trim());
    }

    // Se houver anexo, preparar os dados do arquivo
    if (attachmentData) {
      console.log("Processando anexo:", attachmentData);
      
      // Verificar se já temos o arquivo real (para áudios gravados)
      if (attachmentData.file) {
        console.log("Usando arquivo real:", attachmentData.file);
        formData.append("file", attachmentData.file, attachmentData.name);
      } else if (attachmentData.url) {
        console.log("Convertendo URL para blob:", attachmentData.url);
        // Converter arquivo para blob se necessário
        const fileBlob = await convertToBlob(attachmentData.url, attachmentData.name);
        
        if (fileBlob) {
          console.log("Blob criado com sucesso:", fileBlob);
          formData.append("file", fileBlob, attachmentData.name);
        } else {
          console.error("Falha ao converter arquivo para blob");
        }
      }
    }

    // Log do FormData final
    console.log("FormData preparado:");
    for (let [key, value] of formData.entries()) {
      if (value instanceof File) {
        console.log(`${key}:`, {
          name: value.name,
          size: value.size,
          type: value.type,
        });
      } else {
        console.log(`${key}:`, value);
      }
    }

    return formData;
  };

  const handleSendMessage = async () => {
    if (!leadId || (!newMessage.trim() && !attachment)) return;

    // Verificar se há conexão WhatsApp antes de tentar enviar
    if (!hasWhatsAppConnection) {
      alert("Não é possível enviar mensagens. Configure uma conexão WhatsApp para este lead.");
      return;
    }

    try {
      console.log('🚀 Preparando envio da mensagem...');
      
      // Preparar dados no formato FormData
      const originalType = attachment?.originalMediaType || attachmentType;
      const finalType =
        sendAsDocument &&
        (originalType === "image" || originalType === "video")
          ? "document"
          : attachmentType;

      const messageType = attachment ? finalType : "chat";
      const formData = await prepareFormData(newMessage, attachment, messageType);

      // Simplificar: usar apenas os parâmetros necessários
      await sendMessageMutation.mutateAsync({
        leadId,
        formData
      });

      // Limpar formulário apenas se o envio foi bem-sucedido
      setNewMessage("");
      setAttachment(null);
      setAttachmentType(null);
      setSendAsDocument(false);
      setRecordingTime(0);

      // Scroll para a nova mensagem
      setTimeout(scrollToBottom, 100);

    } catch (error) {
      console.error("Erro final ao enviar mensagem:", error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    // Simular indicador de digitação
    setIsTyping(true);

    // Limpar timeout anterior
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Parar indicador após 2 segundos sem digitar
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 2000);
  };

  // Emojis populares para WhatsApp
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      const mediaType = getMediaType(file);
      setAttachmentType(mediaType);
      setAttachment({
        url: URL.createObjectURL(file),
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
      });
    }
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "video/mp4",
      "video/webm",
      "video/ogg",
      "audio/mp3",
      "audio/wav",
      "audio/ogg",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];

    return allowedTypes.includes(file.type);
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file, "general");
    }

    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    e.target.value = "";
  };

  const toggleAttachmentMenu = () => {
    setShowAttachmentMenu(!showAttachmentMenu);
  };

  const handleAttachmentOptionClick = (option) => {
    setShowAttachmentMenu(false);

    // Criar input de arquivo dinamicamente com as configurações apropriadas
    const input = document.createElement("input");
    input.type = "file";
    input.style.display = "none";

    switch (option) {
      case "document":
        input.accept = ".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,image/*,video/*,audio/*";
        break;
      case "photos":
        input.accept = "image/*,video/*";
        input.multiple = false; // Por enquanto, apenas um arquivo
        break;
      case "audio":
        input.accept = "audio/*";
        break;
      default:
        input.accept = "*/*";
    }

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        handleFileSelection(file, option);
      }
      document.body.removeChild(input);
    };

    document.body.appendChild(input);
    input.click();
  };

  const handleFileSelection = (file, sourceOption) => {
    if (!validateFileType(file)) {
      alert(
        "Tipo de arquivo não suportado. Por favor, selecione um arquivo válido."
      );
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      // 50MB limit
      alert("Arquivo muito grande. Limite máximo: 50MB");
      return;
    }

    const mediaType = getMediaType(file);

    // Se vier da opção "Documento", sempre enviar como documento
    if (sourceOption === "document") {
      setSendAsDocument(true);
      setAttachmentType("document");
      setAttachment({
        url: URL.createObjectURL(file),
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        originalMediaType: mediaType,
        sourceOption: sourceOption,
      });
      return;
    }

    // Se vier da câmera, marcar como tal
    if (sourceOption === "camera") {
      setSendAsDocument(false);
      setAttachmentType(mediaType);
      setAttachment({
        url: URL.createObjectURL(file),
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        originalMediaType: mediaType,
        sourceOption: "camera",
      });
      return;
    }

    // Para imagens e vídeos das outras opções, enviar como mídia
    if (
      (mediaType === "image" || mediaType === "video") &&
      sourceOption !== "document"
    ) {
      setSendAsDocument(false);
      setAttachmentType(mediaType);
      setAttachment({
        url: URL.createObjectURL(file),
        name: file.name,
        size: formatFileSize(file.size),
        type: file.type,
        originalMediaType: mediaType,
        sourceOption: sourceOption,
      });
      return;
    }

    // Para outros tipos, processar normalmente
    setSendAsDocument(false);
    setAttachmentType(mediaType);
    setAttachment({
      url: URL.createObjectURL(file),
      name: file.name,
      size: formatFileSize(file.size),
      type: file.type,
      originalMediaType: mediaType,
      sourceOption: sourceOption,
    });
  };

  // Função para formatar texto do WhatsApp
  const formatWhatsAppText = (text) => {
    if (!text) return text;

    let formattedText = text;

    // Links: http(s):// ou www.
    formattedText = formattedText.replace(/(https?:\/\/[^\s]+|www\.[^\s]+)/g, function(url) {
      let href = url;
      if (!href.match(/^https?:\/\//)) {
        href = 'http://' + href;
      }
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="whatsapp-link">${url}</a>`;
    });

    // Negrito: *texto*
    formattedText = formattedText.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');

    // Itálico: _texto_
    formattedText = formattedText.replace(/_([^_]+)_/g, '<em>$1</em>');

    // Tachado: ~texto~
    formattedText = formattedText.replace(/~([^~]+)~/g, '<del>$1</del>');

    // Monoespaçado: ```texto```
    formattedText = formattedText.replace(/```([^`]+)```/g, '<code class="whatsapp-code-block">$1</code>');

    // Linha de código: `texto`
    formattedText = formattedText.replace(/`([^`]+)`/g, '<code class="whatsapp-inline-code">$1</code>');

    // Citação: > texto
    formattedText = formattedText.replace(/^> (.+)$/gm, '<blockquote class="whatsapp-quote">$1</blockquote>');

    // Lista com marcadores: * texto ou - texto
    formattedText = formattedText.replace(/^[*-] (.+)$/gm, '<li class="whatsapp-bullet">$1</li>');
    
    // Envolver listas em ul se houver itens
    if (formattedText.includes('<li class="whatsapp-bullet">')) {
      formattedText = formattedText.replace(/((<li class="whatsapp-bullet">[^<]*<\/li>\s*)+)/g, '<ul class="whatsapp-list">$1</ul>');
    }

    // Lista enumerada: 1. texto
    formattedText = formattedText.replace(/^(\d+)\. (.+)$/gm, '<li class="whatsapp-numbered" data-number="$1">$2</li>');
    
    // Envolver listas numeradas em ol se houver itens
    if (formattedText.includes('<li class="whatsapp-numbered"')) {
      formattedText = formattedText.replace(/((<li class="whatsapp-numbered"[^<]*<\/li>\s*)+)/g, '<ol class="whatsapp-numbered-list">$1</ol>');
    }

    // Quebras de linha
    formattedText = formattedText.replace(/\n/g, '<br>');

    return formattedText;
  };

  // Função auxiliar para renderizar texto formatado
  const renderFormattedText = (text, className = "", style = {}) => {
    if (!text) return null;
    return (
      <div 
        className={`whatsapp-formatted-text ${className}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: formatWhatsAppText(text) }}
      />
    );
  };

  const renderMessageContent = (message) => {
    // Mensagens de texto simples
    if (!message.hasMedia && message.type === "chat") {
      return renderFormattedText(message.body);
    }

    // Mensagens com mídia
    if (message.hasMedia) {
      // Renderização especial para imagens - com cache inteligente
      if (message.type === 'image') {
        const cachedUrl = cachedMediaUrls[message.serialized_id];
        
        return (
          <div className="message-media">
            <div 
              className="message-inline-image"
              onClick={() => openImageViewer(message.serialized_id, message.type, message.body)}
            >
              {loadingMedia[message.serialized_id] ? (
                <div className="image-loading">
                  <div style={{
                    width: '100%',
                    height: '120px',
                    background: '#f0f2f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px'
                  }}>
                    <div style={{ textAlign: 'center', color: '#666' }}>
                      <i className="fa fa-spinner fa-spin" style={{ fontSize: '1.5rem', marginBottom: '8px', display: 'block' }}></i>
                      <span style={{ fontSize: '0.8rem' }}>Carregando...</span>
                    </div>
                  </div>
                </div>
              ) : cachedUrl ? (
                // Mostrar imagem real se estiver em cache
                <>
                  <img 
                    src={cachedUrl}
                    alt="Imagem"
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  />
                  <div className="image-overlay">
                    <i className="fa fa-search-plus"></i>
                  </div>
                </>
              ) : (
                // Preview estático sem carregamento automático
                <>
                  <div style={{
                    width: '100%',
                    height: '120px',
                    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    position: 'relative',
                    cursor: 'pointer'
                  }}>
                    <div style={{ 
                      textAlign: 'center', 
                      color: 'white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <i className="fa fa-image" style={{ fontSize: '2rem' }}></i>
                      <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                        Clique para visualizar
                      </span>
                    </div>
                  </div>
                  <div className="image-overlay">
                    <i className="fa fa-search-plus"></i>
                  </div>
                </>
              )}
            </div>
            {message.body && (
              <p className="message-caption mt-2" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                {message.body}
              </p>
            )}
          </div>
        );
      }

      // Renderização para vídeos - com cache inteligente
      if (message.type === 'video') {
        const cachedUrl = cachedMediaUrls[message.serialized_id];
        
        return (
          <div className="message-media">
            <div 
              className="message-inline-image"
              onClick={() => openImageViewer(message.serialized_id, message.type, message.body)}
            >
              {loadingMedia[message.serialized_id] ? (
                <div className="image-loading">
                  <div style={{
                    width: '100%',
                    height: '120px',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    border: '2px dashed #dee2e6'
                  }}>
                    <div style={{ textAlign: 'center', color: '#6c757d' }}>
                      <i className="fa fa-video" style={{ 
                        fontSize: '2rem', 
                        marginBottom: '8px', 
                        display: 'block',
                        opacity: 0.7 
                      }}></i>
                      <span style={{ fontSize: '0.8rem', fontWeight: '500' }}>Carregando vídeo...</span>
                    </div>
                  </div>
                </div>
              ) : cachedUrl ? (
                // Mostrar thumbnail do vídeo se estiver em cache
                <>
                  <video 
                    src={cachedUrl}
                    style={{
                      width: '100%',
                      maxHeight: '200px',
                      objectFit: 'cover',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                    muted
                    preload="metadata"
                  />
                  <div className="image-overlay">
                    <i className="fa fa-play"></i>
                  </div>
                  <div className="media-type-badge">
                    <i className="fa fa-video"></i>
                    <span>Vídeo</span>
                  </div>
                </>
              ) : (
                // Preview estático
                <>
                  <div style={{ 
                    width: '100%', 
                    height: '120px', 
                    background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    flexDirection: 'column',
                    gap: '8px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      pointerEvents: 'none'
                    }}></div>
                    <i className="fa fa-play" style={{ 
                      fontSize: '2.5rem', 
                      color: 'white',
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                      zIndex: 1
                    }}></i>
                    <span style={{ 
                      color: 'white', 
                      fontSize: '0.85rem', 
                      fontWeight: '500',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      zIndex: 1
                    }}>
                      Toque para reproduzir
                    </span>
                  </div>
                  <div className="media-type-badge">
                    <i className="fa fa-video"></i>
                    <span>Vídeo</span>
                  </div>
                </>
              )}
            </div>
            {message.body && (
              <p className="message-caption mt-2" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                {message.body}
              </p>
            )}
          </div>
        );
      }

      // Renderização especial para áudios (ptt e audio)
      if (message.type === 'ptt' || message.type === 'audio') {
        const cachedUrl = cachedMediaUrls[message.serialized_id];
        const audioPlayer = audioPlayers[message.serialized_id];
        const isPlaying = audioPlayer && !audioPlayer.paused;
        const isLoadingAudio = audioLoading[message.serialized_id];

        return (
          <div className="message-media">
            <div className="audio-message-container">
              <div className="audio-preview">
                <div className="audio-icon-container">
                  <button
                    className="audio-play-button"
                    onClick={() => handleAudioPlay(message.serialized_id, message.type, message.body)}
                    disabled={isLoadingAudio}
                    title={cachedUrl ? (isPlaying ? "Pausar áudio" : "Reproduzir áudio") : "Baixar e reproduzir áudio"}
                  >
                    {isLoadingAudio ? (
                      <i className="fa fa-spinner fa-spin"></i>
                    ) : cachedUrl ? (
                      <i className={isPlaying ? "fa fa-pause" : "fa fa-play"}></i>
                    ) : (
                      <i className="fa fa-download"></i>
                    )}
                  </button>
                </div>
                
                <div className="audio-info">
                  <div className="audio-type">
                    {message.type === 'ptt' ? 'Mensagem de Voz' : 'Áudio'}
                  </div>
                  {cachedUrl && audioPlayer && !isNaN(audioPlayer.duration) && (
                    <div className="audio-duration">
                      {Math.floor(audioPlayer.duration / 60)}:{Math.floor(audioPlayer.duration % 60).toString().padStart(2, '0')}
                    </div>
                  )}
                </div>

                <div className="audio-wave">
                  <div className={`wave-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`wave-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`wave-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`wave-bar ${isPlaying ? 'playing' : ''}`}></div>
                  <div className={`wave-bar ${isPlaying ? 'playing' : ''}`}></div>
                </div>
              </div>
              
              {message.body && (
                <p className="message-caption mt-2" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                  {message.body}
                </p>
              )}
            </div>
          </div>
        );
      }

      // Renderização especial para documentos
      if (message.type === 'document') {
        const isLoadingDocument = loadingMedia[message.serialized_id];
        
        // Função para obter extensão do arquivo
        const getFileExtension = (body) => {
          if (!body) return 'DOC';
          const parts = body.split('.');
          if (parts.length > 1) {
            return parts[parts.length - 1].toUpperCase();
          }
          return 'DOC';
        };

        // Função para obter cor baseada na extensão
        const getDocumentColor = (extension) => {
          switch (extension.toLowerCase()) {
            case 'pdf':
              return '#dc3545'; // Vermelho
            case 'doc':
            case 'docx':
              return '#2b5ce6'; // Azul
            case 'xls':
            case 'xlsx':
              return '#28a745'; // Verde
            case 'ppt':
            case 'pptx':
              return '#fd7e14'; // Laranja
            case 'txt':
              return '#6c757d'; // Cinza
            default:
              return '#6f42c1'; // Roxo
          }
        };

        const extension = getFileExtension(message.body);
        const documentColor = getDocumentColor(extension);

        return (
          <div className="message-media">
            <div className="document-message-container">
              <div className="document-preview">
                <div className="document-icon-container" style={{ backgroundColor: documentColor }}>
                  <div className="document-icon">
                    <i className="fa fa-file-alt"></i>
                  </div>
                  <div className="document-extension">
                    {extension}
                  </div>
                </div>
                
                <div className="document-info">
                  <div className="document-name">
                    {message.body || 'Documento'}
                  </div>
                  <div className="document-meta">
                    <span>1 página • {extension} • 6 KB</span>
                  </div>
                </div>

                <div className="document-actions">
                  {cachedMediaUrls[message.serialized_id] ? (
                    <a
                      href={cachedMediaUrls[message.serialized_id]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="document-download-button"
                      title="Abrir documento em nova aba"
                      style={{ textDecoration: 'none', color: 'inherit', display: 'inline-block' }}
                    >
                      <i className="fa fa-external-link-alt"></i>
                    </a>
                  ) : (
                    <button
                      className="document-download-button"
                      onClick={() => downloadMedia(message.serialized_id, message.type, message.body)}
                      disabled={isLoadingDocument}
                      title="Baixar documento"
                    >
                      {isLoadingDocument ? (
                        <i className="fa fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fa fa-download"></i>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Renderização especial para stickers
      if (message.type === 'sticker') {
        // Carregar sticker automaticamente se ainda não foi carregado
        const stickerLoaded = cachedMediaUrls[message.serialized_id];
        const isLoading = loadingMedia[message.serialized_id];
        
        if (!stickerLoaded && !isLoading) {
          // Carregar automaticamente
          setLoadingMedia(prev => ({ ...prev, [message.serialized_id]: true }));
          loadMessageMedia(message.serialized_id).then(url => {
            if (url) {
              setCachedMediaUrls(prev => ({
                ...prev,
                [message.serialized_id]: url
              }));
            }
            setLoadingMedia(prev => ({ ...prev, [message.serialized_id]: false }));
          }).catch(() => {
            setLoadingMedia(prev => ({ ...prev, [message.serialized_id]: false }));
          });
        }

        return (
          <div className="message-sticker">
            <div className="sticker-container">
              {isLoading ? (
                <div className="sticker-loading">
                  <div className="sticker-loading-spinner">
                    <i className="fa fa-spinner fa-spin"></i>
                  </div>
                  <span>Carregando sticker...</span>
                </div>
              ) : stickerLoaded ? (
                <div className="sticker-image-wrapper">
                  <img
                    src={stickerLoaded}
                    alt="Sticker"
                    className="sticker-image"
                    onLoad={() => {
                      // Scroll apenas para mensagens recentes (offset 0) e só na primeira vez
                      if (messagesByOffset.has(0) && isFirstTimeOpening) {
                        const recentMessages = messagesByOffset.get(0);
                        if (recentMessages.some(msg => msg.id === message.id)) {
                          scrollToBottom();
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="sticker-placeholder">
                  <div className="sticker-placeholder-content">
                    <div className="sticker-icon-wrapper">
                      <i className="fa fa-smile sticker-icon"></i>
                    </div>
                    <span>Sticker</span>
                    <small>Carregando...</small>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      // Renderização para outros tipos de mídia
      const getMediaIcon = (type) => {
        switch (type) {
          default:
            return 'fa fa-paperclip';
        }
      };

      return (
        <div className="message-media">
          <div className="media-container">
            <div className="media-preview">
              <i className={getMediaIcon(message.type)} style={{ fontSize: '2rem', color: '#25D366' }}></i>
              <div className="media-info">
                <span className="media-type">
                  {message.type === 'album' && 'Álbum de Fotos'}
                  {!['image', 'video', 'ptt', 'audio', 'document', 'sticker', 'album'].includes(message.type) && 'Arquivo'}
                </span>
                <button 
                  className="btn btn-sm btn-outline-primary mt-1"
                  onClick={() => downloadMedia(message.serialized_id, message.type, message.body)}
                  disabled={loadingMedia[message.serialized_id]}
                  title="Clique para visualizar/baixar"
                >
                  {loadingMedia[message.serialized_id] ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-1"></i>
                      Carregando...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-download me-1"></i>
                      Baixar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
          {message.body && (
            <p className="message-caption mt-2" style={{ fontSize: '0.9rem', opacity: 0.8 }}>
              {message.body}
            </p>
          )}
        </div>
      );
    }

    // Mensagens de localização
    if (message.type === 'location' || message.location_latitude) {
      return (
        <div className="message-location">
          <div className="location-container">
            <i className="fa fa-map-marker-alt" style={{ fontSize: '2rem', color: '#25D366' }}></i>
            <div className="location-info">
              <span className="location-type">Localização</span>
              {message.location_name && (
                <div className="location-name">{message.location_name}</div>
              )}
              {message.location_address && (
                <div className="location-address">{message.location_address}</div>
              )}
              <button 
                className="btn btn-sm btn-outline-primary mt-1"
                onClick={() => {
                  if (message.location_latitude && message.location_longitude) {
                    const url = `https://www.google.com/maps?q=${message.location_latitude},${message.location_longitude}`;
                    window.open(url, '_blank');
                  } else if (message.location_url) {
                    window.open(message.location_url, '_blank');
                  }
                }}
                title="Abrir no Google Maps"
              >
                <i className="fa fa-external-link-alt me-1"></i>
                Ver Localização
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Outros tipos de mensagem
    return (
      <div className="message-other">
        <p><em>Mensagem do tipo: {message.type}</em></p>
        {message.body && (
          <div 
            className="whatsapp-formatted-text"
            dangerouslySetInnerHTML={{ __html: formatWhatsAppText(message.body) }}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      dynamicTitle={`WhatsApp - ${leadName || 'Cliente'}`}
      visible={visible}
      onHide={() => setVisible(false)}
    >
      <div
        className={`whatsapp-conversation ${dragOver ? "drag-over" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {dragOver && (
          <div className="drag-overlay">
            <div className="drag-message">
              <i className="fa fa-cloud-upload-alt"></i>
              <p>Solte o arquivo aqui para enviar</p>
            </div>
          </div>
        )}

        {isLoadingMessages ? (
          <div className="loading-messages">
            <i className="fa fa-spinner fa-spin"></i>
            <p>Carregando mensagens...</p>
          </div>
        ) : !hasWhatsAppConnection && connectionError ? (
          <div className="whatsapp-connection-error">
            <i className="fa-brands fa-whatsapp"></i>
            <h3>WhatsApp não configurado</h3>
            <p>{connectionError}</p>
            <small>
              Configure uma conexão do WhatsApp para este interesse para poder enviar mensagens.
            </small>
            <Button
              className="retry-connection-btn"
              icon="pi pi-refresh"
              label="Tentar novamente"
              onClick={() => {
                isManualRetryRef.current = true; // Marca como tentativa manual
                setShouldStopPolling(false); // Reabilita polling temporariamente
                refetchMessages(); // Tenta buscar mensagens novamente
              }}
              severity="secondary"
              size="small"
            />
          </div>
        ) : messages.length === 0 ? (
          <div className="no-messages">
            <i className="fa-brands fa-whatsapp"></i>
            <p>Nenhuma mensagem encontrada</p>
            <small>Inicie a conversa enviando uma mensagem</small>
          </div>
        ) : (
          <>
            <div className="load-more-messages-container">
              {hasMoreMessages ? (
                <Button
                  className="load-more-btn"
                  icon={loadingMoreMessages ? "pi pi-spin pi-spinner" : "pi pi-chevron-up"}
                  label={
                    loadingMoreMessages 
                      ? "Carregando mensagens antigas..." 
                      : "Ver mensagens anteriores"
                  }
                  onClick={loadMoreMessages}
                  disabled={loadingMoreMessages || loadMoreMessagesMutation.isLoading}
                  severity="info"
                  size="small"
                  outlined
                />
              ) : (
                <div className="no-more-messages">
                  <i className="pi pi-check-circle"></i>
                  <span>Todas as mensagens foram carregadas</span>
                </div>
              )}
            </div>
            {messages.map((message, index) => (
              <div
                key={message.id}
                className={`message ${
                  message.from_me ? "sent" : "received"
                } ${message.isOptimistic ? "optimistic" : ""} message-fade-in`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
              {!message.from_me && (
                <div className="message-avatar">
                  <img
                    src={getContactAvatar(message.from, message.from_me, message)}
                    alt={getContactName(message.from, message.from_me, message)}
                    title={getContactName(message.from, message.from_me, message)}
                    className={`avatar-tooltip-${message.id}`}
                    data-pr-tooltip={getContactName(message.from, message.from_me, message)}
                    data-pr-position="top"
                  />
                  <Tooltip target={`.avatar-tooltip-${message.id}`} />
                </div>
              )}

              <div className="message-content">
                {!message.from_me && (
                  <div className="message-sender">
                    {getContactName(message.from, message.from_me, message)}
                  </div>
                )}

                <div className="message-body">
                  {renderMessageContent(message)}
                </div>

                <div className="message-footer">
                  <span className="message-timestamp">
                    {message.isOptimistic ? "Enviando..." : formatTimestamp(message.timestamp)}
                  </span>
                  {message.from_me && (
                    <div className="message-status">
                      {message.isOptimistic ? (
                        <i className="fa fa-clock" title="Enviando" style={{ color: '#9e9e9e' }}></i>
                      ) : (
                        renderMessageStatusIcon(message.ack)
                      )}
                    </div>
                  )}
                </div>
              </div>

              {message.from_me && (
                <div className="message-avatar">
                  <img
                    src={getContactAvatar(message.from, message.from_me, message)}
                    alt={getContactName(message.from, message.from_me, message)}
                    title={getContactName(message.from, message.from_me, message)}
                    className={`avatar-tooltip-sent-${message.id}`}
                    data-pr-tooltip={getContactName(message.from, message.from_me, message)}
                    data-pr-position="top"
                  />
                  <Tooltip target={`.avatar-tooltip-sent-${message.id}`} />
                </div>
              )}
            </div>
            ))}
          </>
        )}

        {isTyping && (
          <div className="message sent typing-indicator">
            <div className="message-content">
              <div className="message-body">
                <div className="typing-animation">
                  <div className="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="typing-text">digitando...</span>
                </div>
              </div>
            </div>
            <div className="message-avatar">
              <img
                src={getContactAvatar("me", true)}
                alt="Você"
              />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Preview de anexo acima do input */}
      {attachment && (
        <div className="attachment-preview-top">
          <div className="preview-container">
            {/* Preview para imagem como mídia */}
            {attachmentType === "image" && !sendAsDocument && (
              <div className="preview-card image-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <div className="preview-thumbnail">
                  <img
                    src={attachment.url}
                    alt="Preview"
                    className="preview-image"
                    onClick={() => setImageViewer({ 
                      visible: true, 
                      url: attachment.url, 
                      title: attachment.name,
                      type: 'image' 
                    })}
                  />
                  <div className="preview-overlay">
                    <i className="fa fa-search-plus"></i>
                    <span>Clique para visualizar</span>
                  </div>
                  {attachment.sourceOption === "camera" && (
                    <div className="preview-source-badge camera-badge">
                      <i className="fa fa-camera"></i>
                      <span>Câmera</span>
                    </div>
                  )}
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">{attachment.name}</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge image-badge">
                    <i className="fa fa-image"></i>
                    <span>Imagem</span>
                  </div>
                  <div className="preview-description">
                    {attachment.sourceOption === "camera" 
                      ? "Foto capturada pela câmera" 
                      : "Será enviada com compressão automática do WhatsApp"}
                  </div>
                </div>
              </div>
            )}

            {/* Preview para vídeo como mídia */}
            {attachmentType === "video" && !sendAsDocument && (
              <div className="preview-card video-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <div className="preview-thumbnail">
                  <video src={attachment.url} className="preview-video" muted />
                  <div className="preview-overlay">
                    <i className="fa fa-play"></i>
                    <span>Clique para reproduzir</span>
                  </div>
                  {attachment.sourceOption === "camera" && (
                    <div className="preview-source-badge camera-badge">
                      <i className="fa fa-video"></i>
                      <span>Câmera</span>
                    </div>
                  )}
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">{attachment.name}</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge video-badge">
                    <i className="fa fa-video"></i>
                    <span>Vídeo</span>
                  </div>
                  <div className="preview-description">
                    {attachment.sourceOption === "camera" 
                      ? "Vídeo gravado pela câmera" 
                      : "Será enviado com compressão automática do WhatsApp"}
                  </div>
                </div>
              </div>
            )}

            {/* Preview para imagem/vídeo como documento */}
            {(attachmentType === "image" || attachmentType === "video") && sendAsDocument && (
              <div className="preview-card document-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <div className="preview-thumbnail document-thumb">
                  <div className="document-icon">
                    <i className="fa fa-file-alt"></i>
                  </div>
                  <div className="document-type">
                    {attachment.originalMediaType === "image" ? "IMG" : "VID"}
                  </div>
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">{attachment.name}</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge document-badge">
                    <i className="fa fa-file"></i>
                    <span>Documento</span>
                  </div>
                  <div className="preview-description">
                    Será enviado sem compressão, mantendo qualidade original
                  </div>
                </div>
              </div>
            )}
            {/* Preview para áudio PTT */}
            {attachmentType === "ptt" && (
              <div className="preview-card audio-preview ptt-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <button 
                  className="download-preview-btn"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = attachment.url;
                    link.download = `mensagem_de_voz_${Date.now()}.webm`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  title="Baixar áudio gravado"
                >
                  <i className="fa fa-download"></i>
                </button>
                <div className="preview-thumbnail audio-thumb ptt-thumb">
                  <div className="audio-icon">
                    <i className="fa fa-microphone"></i>
                  </div>
                  <div className="audio-wave">
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                  </div>
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">Mensagem de Voz</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge ptt-badge">
                    <i className="fa fa-microphone"></i>
                    <span>PTT • {formatDuration(attachment.duration)}</span>
                  </div>
                  <div className="preview-description">
                    Áudio gravado será enviado como mensagem de voz
                  </div>
                </div>
              </div>
            )}

            {/* Preview para áudio */}
            {attachmentType === "audio" && (
              <div className="preview-card audio-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <div className="preview-thumbnail audio-thumb">
                  <div className="audio-icon">
                    <i className="fa fa-music"></i>
                  </div>
                  <div className="audio-wave">
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                    <div className="wave-bar"></div>
                  </div>
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">{attachment.name}</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge audio-badge">
                    <i className="fa fa-headphones"></i>
                    <span>Áudio</span>
                  </div>
                  <div className="preview-description">
                    Arquivo de áudio será enviado como anexo
                  </div>
                </div>
              </div>
            )}

            {/* Preview para documento real */}
            {attachmentType === "document" && !attachment.originalMediaType && (
              <div className="preview-card document-preview">
                <button 
                  className="remove-preview-btn"
                  onClick={() => {
                    setAttachment(null);
                    setAttachmentType(null);
                    setSendAsDocument(false);
                  }}
                  title="Remover anexo"
                >
                  <i className="fa fa-times"></i>
                </button>
                <div className="preview-thumbnail document-thumb">
                  <div className="document-icon">
                    <i className="fa fa-file-alt"></i>
                  </div>
                  <div className="document-type">
                    {attachment.name.split('.').pop().toUpperCase()}
                  </div>
                </div>
                <div className="preview-details">
                  <div className="preview-meta">
                    <div className="preview-filename">{attachment.name}</div>
                    <div className="preview-filesize">{attachment.size}</div>
                  </div>
                  <div className="preview-type-badge document-badge">
                    <i className="fa fa-file"></i>
                    <span>Documento</span>
                  </div>
                  <div className="preview-description">
                    Documento será enviado como anexo
                  </div>
                </div>
              </div>
            )}

            {/* Botões de ação */}
            <div className="preview-actions">
              {/* Botão para alternar modo apenas para imagens/vídeos */}
              {(attachment.originalMediaType === "image" ||
                attachment.originalMediaType === "video") && (
                <div className="toggle-mode-section">
                  <div className="toggle-label">Modo de envio:</div>
                  <div className="toggle-buttons">
                    <button
                      className={`mode-button ${!sendAsDocument ? 'active' : ''}`}
                      onClick={() => {
                        setSendAsDocument(false);
                        setAttachmentType(attachment.originalMediaType);
                      }}
                      title="Enviar como mídia (com compressão)"
                    >
                      <i className="fa fa-compress"></i>
                      <span>Mídia</span>
                    </button>
                    <button
                      className={`mode-button ${sendAsDocument ? 'active' : ''}`}
                      onClick={() => {
                        setSendAsDocument(true);
                        setAttachmentType("document");
                      }}
                      title="Enviar como documento (qualidade original)"
                    >
                      <i className="fa fa-file"></i>
                      <span>Documento</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="whatsapp-input-container">
        <input
          type="file"
          id="attachment"
          style={{ display: "none" }}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
          onChange={handleAttachmentChange}
        />

        {/* Botão de anexo com menu aprimorado */}
        <div className="attachment-menu-container">
          <button
            className={`whatsapp-attach-button ${showAttachmentMenu ? 'active' : ''}`}
            onClick={toggleAttachmentMenu}
            disabled={!hasWhatsAppConnection}
            title={!hasWhatsAppConnection ? "Configure uma conexão WhatsApp primeiro" : "Anexar arquivo"}
          >
            <i className={showAttachmentMenu ? "fa fa-times" : "fa fa-paperclip"}></i>
          </button>

          {/* Menu de opções de anexo aprimorado */}
          {showAttachmentMenu && (
            <div className="attachment-menu-enhanced">
              <div className="attachment-menu-header">
                <span>Selecionar arquivo</span>
              </div>
              
              <div className="attachment-options">
                <div
                  className="attachment-option-enhanced attachment-document-option"
                  onClick={() => handleAttachmentOptionClick("document")}
                >
                  <div className="option-icon-enhanced attachment-document-icon">
                    <i className="fa fa-file-alt"></i>
                  </div>
                  <div className="option-content">
                    <span className="option-label">Documento</span>
                    <span className="option-description">PDF, Word, Excel, etc.</span>
                  </div>
                  <div className="option-arrow">
                    <i className="fa fa-chevron-right"></i>
                  </div>
                </div>

                <div
                  className="attachment-option-enhanced photos-option"
                  onClick={() => handleAttachmentOptionClick("photos")}
                >
                  <div className="option-icon-enhanced photos-icon">
                    <i className="fa fa-image"></i>
                  </div>
                  <div className="option-content">
                    <span className="option-label">Fotos e vídeos</span>
                    <span className="option-description">Imagens, GIFs, vídeos</span>
                  </div>
                  <div className="option-arrow">
                    <i className="fa fa-chevron-right"></i>
                  </div>
                </div>

                <div
                  className="attachment-option-enhanced audio-option"
                  onClick={() => handleAttachmentOptionClick("audio")}
                >
                  <div className="option-icon-enhanced audio-icon">
                    <i className="fa fa-headphones"></i>
                  </div>
                  <div className="option-content">
                    <span className="option-label">Áudio</span>
                    <span className="option-description">MP3, WAV, OGG, etc.</span>
                  </div>
                  <div className="option-arrow">
                    <i className="fa fa-chevron-right"></i>
                  </div>
                </div>
              </div>
              
              <div className="attachment-menu-footer">
                <small>Tamanho máximo: 50MB</small>
              </div>
            </div>
          )}
        </div>

        <Tooltip target=".whatsapp-attach-button" position="top" />

        {/* <button
          className={`whatsapp-mode-button ${recordingMode}`}
          onClick={toggleRecordingMode}
          data-pr-tooltip={recordingMode === 'text' ? "Modo: Texto (clique para áudio)" : "Modo: Áudio (clique para texto)"}
          disabled={isRecording}
        >
          <i className={recordingMode === 'text' ? "fa fa-font" : "fa fa-volume-up"}></i>
        </button>
        <Tooltip target=".whatsapp-mode-button" position="top" /> */}

        {/* Indicador de gravação aprimorado */}
        {isRecording && (
          <div className="recording-indicator-enhanced">
            <div className="recording-pulse-enhanced"></div>
            <div className="recording-content">
              <div className="recording-icon">
                <i className={recordingMode === "audio" ? "fa fa-microphone" : "fa fa-font"}></i>
              </div>
              <div className="recording-info">
                <span className="recording-text">
                  {recordingMode === "audio" ? "Gravando áudio" : "Reconhecendo fala"}
                </span>
                <span className="recording-time">
                  {recordingMode === "audio" ? formatRecordingTime(recordingTime) : "Fale agora..."}
                </span>
              </div>
              <div className="recording-action">
                <span>Clique para parar</span>
              </div>
            </div>
          </div>
        )}

        <button
          className={`whatsapp-voice-button ${isRecording ? "recording active" : ""}`}
          onClick={toggleRecording}
          disabled={!hasWhatsAppConnection}
          data-pr-tooltip={
            !hasWhatsAppConnection 
              ? "Configure uma conexão WhatsApp primeiro"
              : isRecording
              ? "Parar gravação"
              : recordingMode === "text"
              ? "Gravar texto por voz"
              : "Gravar mensagem de áudio"
          }
        >
          <div className="voice-button-content">
            {isRecording ? (
              <>
                <i className="fa fa-stop"></i>
                <div className="recording-waves">
                  <div className="wave"></div>
                  <div className="wave"></div>
                  <div className="wave"></div>
                </div>
              </>
            ) : (
              <i className="fa fa-microphone"></i>
            )}
          </div>
        </button>
        <Tooltip target=".whatsapp-voice-button" position="top" />

        <input
          type="text"
          className={`whatsapp-input ${!hasWhatsAppConnection ? 'disabled' : ''}`}
          placeholder={
            !hasWhatsAppConnection
              ? "WhatsApp não configurado - Configure uma conexão primeiro"
              : isRecording
              ? recordingMode === "audio"
                ? "Gravando áudio..."
                : "Falando... O texto aparecerá aqui"
              : attachment
              ? "Digite uma legenda (opcional)"
              : "Digite uma mensagem"
          }
          value={newMessage}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          maxLength={1000}
          disabled={!hasWhatsAppConnection || (isRecording && recordingMode === "audio")}
        />

        {newMessage.length > 900 && (
          <div className="character-counter">{newMessage.length}/1000</div>
        )}

        <button
          className="whatsapp-send-button"
          onClick={handleSendMessage}
          disabled={(!newMessage.trim() && !attachment) || sendMessageMutation.isLoading || !hasWhatsAppConnection}
          data-pr-tooltip={
            !hasWhatsAppConnection
              ? "Configure uma conexão WhatsApp primeiro" 
              : sendMessageMutation.isLoading
              ? "Enviando..."
              : "Enviar mensagem"
          }
        >
          {sendMessageMutation.isLoading ? (
            <i className="fa fa-spinner fa-spin"></i>
          ) : (
            <i className="fa fa-paper-plane"></i>
          )}
        </button>
        <Tooltip target=".whatsapp-send-button" position="top" />
      </div>

      {/* Modal de visualização de imagem */}
      {imageViewer.visible && (
        <div 
          className="image-viewer-modal" 
          onClick={(e) => e.target === e.currentTarget && closeImageViewer()}
        >
          <div className="image-viewer-container">
            <div className="image-viewer-header">
              <div className="image-viewer-info">
                <div className="image-viewer-title">
                  {imageViewer.title || 'Imagem'}
                </div>
                <div className="image-viewer-subtitle">
                  {leadName || 'Cliente'} • {imageViewer.type === 'video' ? 'Vídeo' : 'Imagem'}
                </div>
              </div>
              <div className="image-viewer-controls">
                <button
                  className="image-viewer-button"
                  onClick={() => {
                    // Usar a URL já carregada para download direto
                    const link = document.createElement('a');
                    link.href = imageViewer.url;
                    link.download = `${imageViewer.type}_${Date.now()}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  title="Baixar"
                >
                  <i className="fa fa-download"></i>
                </button>
                <button
                  className="image-viewer-button"
                  onClick={closeImageViewer}
                  title="Fechar (ESC)"
                >
                  <i className="fa fa-times"></i>
                </button>
              </div>
            </div>
            
            {imageViewer.type === 'video' ? (
              <video
                src={imageViewer.url}
                className="image-viewer-image"
                controls
                autoPlay={false}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={imageViewer.url}
                alt={imageViewer.title}
                className="image-viewer-image"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ModalWhatsApp;
